import CorvuDialog from '@corvu/dialog'
import { cn } from '@forest/shared'
import { DialogShell } from '@forest/shared/solid'
import { useQuery } from '@tanstack/solid-query'
import { createEffect, Match, Show, Switch, type Accessor } from 'solid-js'
import { logNonFatal } from '~/lib/diagnostics'
import {
  deepLinkOverlayCover,
  detailOverlayAnimation,
  dialogPanelAnimation,
  panelPresented,
} from '~/lib/dialog-animation'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { m } from '~/paraglide/messages'
import {
  landSkinDetailAssetsQuery,
  type CatalogueProduct,
  type LandSkinProduct,
} from '~/services/catalogue'
import type { ShopBalances } from '~/store/session-ledger'
import { BalancePills } from './balance-pills'
import { createDetailSlidePager, DetailSlidePager } from './detail-slide-pager'
import { DetailUnlockFooter } from './detail-unlock-footer'
import { DialogCloseButton } from './dialog-buttons'

// The land-skin detail (GLOSSARY.md "Land-skin detail"): the tree detail's
// shape without the minute-range phase preview. Title and price come with
// the card; the image area is a pager of the product's detail slides
// (GLOSSARY.md "Detail slide"), one showing at a time with a dot per slide
// beneath. Every slide's image loads on open behind its own spinner and is
// left blank on failure with diagnostics and no fallback to another asset
// (ADR 0013). The showing slide's suffix rides on the title; beneath it the
// description hydrates on open from the native string catalog (ADR 0020).
export function LandSkinDetail(props: {
  balances: Accessor<ShopBalances | undefined>
  // Whether the launch deep link auto-opened this detail (GLOSSARY.md "Shop
  // launch deep link"). Its overlay is a solid cover over the catalogue
  // rather than the frame's blurred glass.
  fromDeepLink?: boolean
  open: boolean
  onClose: () => void
  // Fires once the open dialog is presented: the description settled —
  // loaded or failed — the showing slide loaded or failed and decoded, and
  // the panel's enter animation finished. The launch deep link's Shop reveal
  // waits on it (GLOSSARY.md "Reveal surface"). Once per mount.
  onPresented?: () => void
  onPurchase: (product: CatalogueProduct) => void
  onTopUp: (currency: CatalogueCurrency) => void
  product: LandSkinProduct
  serverNow: Accessor<number>
  // A free unlock in flight (GLOSSARY.md "Free product"): the detail is its
  // pending surface, so it cannot be dismissed until the command settles.
  unlockPending?: boolean
}) {
  const slides = () => props.product.detailSlides
  // Detail assets hydrate on demand (ADR 0020): the dialog opens on grid data
  // alone; only the description waits on this query.
  const assets = useQuery(() => landSkinDetailAssetsQuery(props.product.gid))
  const pager = createDetailSlidePager({
    imageUrls: () => slides().map((slide) => slide.imageUrl),
    open: () => props.open,
    resetKey: () => props.product.id,
  })
  // The showing slide's suffix rides on the title. An empty title (a native
  // l10n miss) takes no suffix: a lone " · Home" names nothing.
  const title = () => {
    const base = props.product.title
    const showing = slides()[pager.index()] ?? slides()[0]
    return base ? `${base}${showing.titleSuffix}` : ''
  }

  const reportImageFailure = (slideIndex: number, url: string) => {
    logNonFatal('shop_land_skin_detail_image_failed', {
      gid: String(props.product.gid),
      index: String(slideIndex),
      product_id: String(props.product.id),
      url,
    })
  }

  // The Shop reveal lands on the showing slide — the first, every open starts
  // there — so only its image must have settled and decoded; the other
  // slides keep loading behind it. Assets settle first so the DOM already
  // holds the description or the inline retry.
  let panel: HTMLElement | undefined
  let showingSlide: HTMLElement | undefined
  let presented = false
  createEffect(() => {
    if (presented || !props.open) return
    if (assets.data === undefined && !assets.isError) return
    if (!pager.firstSlideSettled()) return
    presented = true
    const onPresented = props.onPresented
    if (!onPresented) return
    const element = panel
    if (!element) {
      onPresented()
      return
    }
    void panelPresented(element, showingSlide ?? element).then(onPresented)
  })

  return (
    <DialogShell
      ref={(element) => {
        panel = element
      }}
      open={props.open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !props.unlockPending) props.onClose()
      }}
      closeOnEscapeKeyDown={false}
      testId="land-skin-detail"
      class={`z-30 flex w-[300px] flex-col items-center gap-4 p-6 text-center text-[#333] outline-none ${dialogPanelAnimation}`}
      overlayClass={cn(
        detailOverlayAnimation,
        props.fromDeepLink && deepLinkOverlayCover,
      )}
      overlayContent={
        <BalancePills
          balances={props.balances()}
          class="fixed right-2 top-[calc(8px+var(--safe-area-top))] z-[31] duration-300 animate-in fade-in"
          inverted
          testIdPrefix="land-skin-detail-"
        />
      }
    >
      <>
        <DialogCloseButton
          disabled={!props.open || props.unlockPending}
          onClose={() => props.onClose()}
        />

        {/* The swipe area is everything above the footer, so the Unlock
            button never doubles as a gesture target. */}
        <DetailSlidePager
          dotsPosition="above-copy"
          firstSlideRef={(element) => {
            showingSlide = element
          }}
          onImageFailure={reportImageFailure}
          pager={pager}
          testIdPrefix="land-skin-detail"
        >
          <div class="mt-6 flex w-full flex-col items-center gap-2">
            <CorvuDialog.Label
              id="land-skin-detail-title"
              class="w-full text-base font-semibold text-ink"
            >
              {title()}
            </CorvuDialog.Label>
            <Switch
              fallback={
                <div
                  data-testid="land-skin-detail-description-skeleton"
                  aria-hidden="true"
                  class="h-[22px] w-3/4 animate-pulse rounded-full bg-black/10"
                />
              }
            >
              <Match when={assets.data}>
                {(data) => (
                  // A miss is empty copy and no paragraph at all — never the
                  // raw key (ADR 0012).
                  <Show when={data().description}>
                    {(description) => (
                      <CorvuDialog.Description
                        data-testid="land-skin-detail-description"
                        class="w-full text-sm text-[#00524F]/70"
                      >
                        {description()}
                      </CorvuDialog.Description>
                    )}
                  </Show>
                )}
              </Match>
              <Match when={assets.isError && !assets.isFetching}>
                <div
                  data-testid="land-skin-detail-assets-error"
                  class="flex w-full flex-col items-center gap-2"
                >
                  <span class="text-sm leading-[22px] text-[#666]">
                    {m.dialog_something_went_wrong_title()}
                  </span>
                  <button
                    type="button"
                    data-testid="land-skin-detail-assets-retry"
                    onClick={() => void assets.refetch()}
                    class="rounded-full bg-white/60 px-4 py-1.5 text-sm font-semibold leading-4 text-[#176361]"
                  >
                    {m.retry()}
                  </button>
                </div>
              </Match>
            </Switch>
          </div>
        </DetailSlidePager>

        <DetailUnlockFooter
          balances={props.balances}
          onPurchase={props.onPurchase}
          onTopUp={props.onTopUp}
          pending={props.unlockPending}
          product={props.product}
          serverNow={props.serverNow}
          testIdPrefix="land-skin-detail"
        />
      </>
    </DialogShell>
  )
}
